import React, { type SetStateAction, type Dispatch, type MouseEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';
import { Flex } from '@atlaskit/primitives/compiled';
import ShrinkVerticalIcon from '@atlaskit/icon/core/shrink-vertical';

import { ViewReplyOptions } from '@confluence/comments-common/entry-points/repliesHelpers';

const styles = cssMap({
	buttonsContainer: {
		justifyContent: 'space-between',
		paddingLeft: token('space.300'),
		paddingTop: token('space.100'),
	},
});

const messages = defineMessages({
	hideReplies: {
		id: 'comments-common.comments.replies.footer.hideReplies',
		defaultMessage: '{numOfVisibleReplies, plural, one {Hide reply} other {Hide replies}}',
		description: 'Label for the button to hide comment replies',
	},
});

type RepliesFooterActionsProps = {
	replyView: ViewReplyOptions;
	setReplyView: Dispatch<SetStateAction<ViewReplyOptions>>;
	numOfVisibleReplies: number;
};

export const RepliesFooter = ({
	replyView,
	setReplyView,
	numOfVisibleReplies,
}: RepliesFooterActionsProps) => {
	const { formatMessage } = useIntl();

	const onHideRepliesClickHandler = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();

		setReplyView(ViewReplyOptions.HIDDEN);
	};

	const isHideRepliesToggleVisible =
		replyView !== ViewReplyOptions.HIDDEN && numOfVisibleReplies > 1;

	return (
		<Flex testId="replies-footer" xcss={styles.buttonsContainer} alignItems="center">
			{/* Reply button to be added */}
			{isHideRepliesToggleVisible && (
				<Button
					appearance="subtle"
					iconBefore={ShrinkVerticalIcon}
					onClick={onHideRepliesClickHandler}
				>
					{formatMessage(messages.hideReplies, { numOfVisibleReplies })}
				</Button>
			)}
		</Flex>
	);
};
